import type { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { useSessionRun } from '../hooks/use-api';
import { StatGrid } from '../components/shared/StatGrid';
import { Spinner } from '../components/shared/Spinner';
import styles from './SessionReplayPage.module.css';

const formatCost = (microCents: number): string => {
  const dollars = microCents / 1_000_000;
  return `$${dollars.toFixed(4)}`;
};

const SessionReplayPage = (): ReactElement => {
  const { scheduleId, runId } = useParams();
  const run = useSessionRun(scheduleId ?? '', runId ?? '');

  if (run.isLoading) {
    return (
      <div className={styles.page}>
        <Spinner />
      </div>
    );
  }

  if (run.isError || !run.data) {
    return (
      <div className={styles.page}>
        <button data-testid="back" className={styles.backBtn} onClick={(): void => { history.back(); }}>
          &larr; Back
        </button>
        <div className={styles.error}>
          {run.isError ? 'Failed to load session run.' : 'Session run not found.'}
        </div>
      </div>
    );
  }

  const data = run.data;
  const progressEntries = Object.entries(data.completionProgress);

  const statItems = [
    { label: 'Actions', value: data.actionsExecuted, color: 'accent' as const, onClick: null },
    { label: 'Credits Spent', value: data.creditsSpent, color: 'warning' as const, onClick: null },
    { label: 'Input Tokens', value: data.llmInputTokens.toLocaleString(), color: 'default' as const, onClick: null },
    { label: 'Output Tokens', value: data.llmOutputTokens.toLocaleString(), color: 'default' as const, onClick: null },
    { label: 'Est. Cost', value: formatCost(data.estimatedCostMicro), color: 'default' as const, onClick: null },
  ];

  return (
    <div className={styles.page}>
      <button className={styles.backBtn} onClick={(): void => { history.back(); }}>
        &larr; Back
      </button>

      <div className={styles.header}>
        <h1 className={styles.entity}>{data.entityName}</h1>
        <span className={styles.runLabel}>Run #{data.runNumber}</span>
      </div>

      <section className={styles.stats}>
        <StatGrid items={statItems} />
      </section>

      {progressEntries.length > 0 ? (
        <section className={styles.progress}>
          <h3 className={styles.progressHeading}>Completion Progress</h3>
          <div className={styles.progressList}>
            {progressEntries.map(([key, val]) => (
              <div key={key} className={styles.progressRow}>
                <span className={styles.progressKey}>{key}</span>
                <span className={styles.progressValue}>{val}</span>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
};

export { SessionReplayPage };
